import React, { useState, useEffect, useCallback, useRef } from "react";
import { Chess, Square } from "chess.js";
import { Chessboard, PieceDropHandlerArgs, PieceHandlerArgs } from "react-chessboard";

import {
    Dialog,
    DialogContent,
    Box,
    IconButton,
    Button,
    Typography,
    Alert,
    Tooltip,
    Chip,
    Paper,
    useTheme,
} from "@mui/material";
import { GridView, AccessTime as TimerIcon } from "@mui/icons-material";

import queen from "./images/wQ.svg";
import rook from "./images/wR.svg";
import bishop from "./images/wB.svg";
import knight from "./images/wN.svg";
import { Me } from "./lichess/LichessAuth";
import { CurrentGame, GameFullEvent, GameStateEvent } from './lichess/LichessClient';
import { ChessUser } from "./ChessModels";
import { usePhysicalBoardSync } from "./usePhysicalBoardSync";
import { LastMove, PromotionMove } from "./analysis/types";

export const formatClockTime = (timeMs: number | null | undefined): string => {
    if (timeMs === null || timeMs === undefined || isNaN(timeMs)) return "--:--";
    const totalSeconds = Math.max(0, Math.floor(timeMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }

    if (totalSeconds < 10) {
        const tenths = Math.floor((Math.max(0, timeMs) % 1000) / 100);
        return `0:0${totalSeconds}.${tenths}`;
    }

    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

interface ChessTimerProps {
    timeMs: number | null | undefined;
    isActiveTurn: boolean;
}

const ChessTimer: React.FC<ChessTimerProps> = ({ timeMs, isActiveTurn }) => {
    if (timeMs === null || timeMs === undefined) return null;
    const isLowTime = timeMs <= 20000; // 20s or less

    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                fontFamily: "monospace",
                fontWeight: 700,
                fontSize: "1.05rem",
                letterSpacing: "0.5px",
                backgroundColor: isActiveTurn
                    ? isLowTime
                        ? "rgba(239, 68, 68, 0.25)"
                        : "rgba(34, 197, 94, 0.2)"
                    : "rgba(0, 0, 0, 0.4)",
                color: isActiveTurn
                    ? isLowTime
                        ? "#ef4444"
                        : "#4ade80"
                    : isLowTime
                    ? "#f87171"
                    : "rgba(255, 255, 255, 0.85)",
                border: isActiveTurn
                    ? isLowTime
                        ? "1.5px solid #ef4444"
                        : "1.5px solid #22c55e"
                    : "1px solid rgba(255, 255, 255, 0.12)",
                boxShadow: isActiveTurn
                    ? isLowTime
                        ? "0 0 10px rgba(239, 68, 68, 0.35)"
                        : "0 0 10px rgba(34, 197, 94, 0.3)"
                    : "none",
                transition: "all 0.2s ease-in-out",
            }}
        >
            <TimerIcon sx={{ fontSize: "1.1rem", opacity: 0.9 }} />
            <span>{formatClockTime(timeMs)}</span>
        </Box>
    );
};

interface LichessGameProps {
    me: Me;
    currentGame: CurrentGame;
    onBackToGrid?: () => void;
}

const loadInitialFen = (chessInstance: Chess, initFen: string) => {
    if (initFen && initFen !== "startpos") {
        chessInstance.load(initFen);
    } else {
        chessInstance.reset();
    }
};

const replayMoves = (chessInstance: Chess, moves: string[]): boolean => {
    for (const move of moves) {
        const from = move.slice(0, 2);
        const to = move.slice(2, 4);
        const promotion = move.length > 4 ? move[4] : undefined;
        try {
            const result = chessInstance.move({ from, to, promotion });
            if (!result) return false;
        } catch {
            return false;
        }
    }
    return true;
};

const LichessGame: React.FC<LichessGameProps> = ({ me, currentGame, onBackToGrid }) => {
    const lichessClient = me.lichessClient;
    const initialFenRef = useRef<string>("startpos");
    const lastReceivedMovesRef = useRef<string>("");

    const playerColor = currentGame.color === "black" ? "black" : "white";

    const [chess] = useState<Chess>(() => {
        const c = new Chess();
        if (currentGame?.fen) {
            try {
                c.load(currentGame.fen);
            } catch {
                c.reset();
            }
        }
        return c;
    });

    const [fen, setFen] = useState<string>(() => {
        if (currentGame?.fen) {
            return currentGame.fen;
        }
        return chess.fen();
    });

    const [promotionMove, setPromotionMove] = useState<PromotionMove | null>(null);
    const [promotionVisible, setPromotionVisible] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [history, setHistory] = useState<string[]>([]);
    const [gameStatus, setGameStatus] = useState<string>("");
    const [player, setPlayer] = useState<ChessUser | null>(() => ({ username: me.username }));
    const [opponent, setOpponent] = useState<ChessUser | null>(() => ({
        username: currentGame.opponent?.username || "Opponent",
    }));
    const [playerRating, setPlayerRating] = useState<number | null>(null);
    const [opponentRating, setOpponentRating] = useState<number | null>(() => currentGame.opponent?.rating ?? null);
    const [playerTitle, setPlayerTitle] = useState<string | null>(null);
    const [opponentTitle, setOpponentTitle] = useState<string | null>(() => currentGame.opponent?.title ?? null);

    // Clocks (time in milliseconds)
    const [whiteTimeMs, setWhiteTimeMs] = useState<number | null>(() => {
        if (currentGame.secondsLeft !== undefined) {
            return currentGame.isMyTurn
                ? (playerColor === "white" ? currentGame.secondsLeft * 1000 : null)
                : (playerColor === "black" ? currentGame.secondsLeft * 1000 : null);
        }
        return null;
    });
    const [blackTimeMs, setBlackTimeMs] = useState<number | null>(() => {
        if (currentGame.secondsLeft !== undefined) {
            return currentGame.isMyTurn
                ? (playerColor === "black" ? currentGame.secondsLeft * 1000 : null)
                : (playerColor === "white" ? currentGame.secondsLeft * 1000 : null);
        }
        return null;
    });

    const lastTickRef = useRef<number>(Date.now());

    const hasClocks = whiteTimeMs !== null || blackTimeMs !== null;

    // Live clock ticking effect
    useEffect(() => {
        if (chess.isGameOver() || !hasClocks) {
            return;
        }

        lastTickRef.current = Date.now();
        const activeTurn = chess.turn();

        const interval = setInterval(() => {
            const now = Date.now();
            const delta = now - lastTickRef.current;
            lastTickRef.current = now;

            if (activeTurn === "w") {
                setWhiteTimeMs((prev) => (prev !== null ? Math.max(0, prev - delta) : null));
            } else {
                setBlackTimeMs((prev) => (prev !== null ? Math.max(0, prev - delta) : null));
            }
        }, 100);

        return () => clearInterval(interval);
    }, [chess, fen, hasClocks]);

    const theme = useTheme();

    const sendMoveToLichessRef = useRef<
        (from: string, to: string, promotion: string | null) => Promise<boolean>
    >(async () => false);

    // Synchronize with physical ESP32 board
    const {
        connected,
        customSquareStyles,
        sendBoardSetup,
        setLastMove,
        clearSelection,
    } = usePhysicalBoardSync({
        channelId: "lichess-game",
        chess,
        fen,
        mode: "game",
        playerColor,
        isGameOver: chess.isGameOver(),
        onMoveExecuted: async (move, newFen) => {
            setFen(newFen);
            const moveStr = `${move.from}${move.to}${move.promotion || ""}`;
            setHistory((prev) => [...prev, moveStr]);
            await sendMoveToLichessRef.current(move.from, move.to, move.promotion || null);
        },
        onPromotionNeeded: (promo) => {
            if (chess.isGameOver()) return;
            setPromotionMove(promo);
            setPromotionVisible(true);
        },
    });

    const applyGameState = useCallback((movesStr: string, initFen: string) => {
        lastReceivedMovesRef.current = movesStr || "";
        const moveList = movesStr && movesStr.trim().length > 0
            ? movesStr.trim().split(/\s+/).filter(Boolean)
            : [];

        try {
            loadInitialFen(chess, initFen);
            const ok = replayMoves(chess, moveList);
            if (!ok) {
                setError("Invalid moves received from Chess server. Please refresh the page.");
                return;
            }
            const newFen = chess.fen();
            setFen(newFen);
            setHistory(moveList);
            setError(null);

            let latestMove: LastMove | null = null;
            if (moveList.length > 0) {
                const last = moveList[moveList.length - 1];
                latestMove = {
                    from: last.slice(0, 2) as Square,
                    to: last.slice(2, 4) as Square,
                    promotion: last.length > 4 ? (last[4] as any) : undefined,
                };
            }
            setLastMove(latestMove);
            sendBoardSetup(newFen, false, latestMove);
        } catch (err) {
            console.error("Error applying game state moves:", err);
            setError("Invalid state from Lichess. Please refresh the page.");
        }
    }, [chess, sendBoardSetup, setLastMove]);

    // Send move to lichess API
    const sendMoveToLichess = useCallback(
        async (from: string, to: string, promotion: string | null): Promise<boolean> => {
            if (!currentGame) return false;
            try {
                const res = await lichessClient.makeMove(
                    currentGame.gameId,
                    from,
                    promotion ? `${to}${promotion}` : to
                );
                if (!res.ok) {
                    const errorText = await res.text().catch(() => "");
                    console.error("Failed to make move on Lichess:", res.status, errorText);
                    if (res.status === 400 && chess.isGameOver()) {
                        return false;
                    }
                    setError(`Failed to make move on Lichess (${res.status})`);
                    // Rollback to authoritative state
                    applyGameState(lastReceivedMovesRef.current, initialFenRef.current);
                    return false;
                }
                return true;
            } catch (error) {
                console.error(error);
                if (!chess.isGameOver()) {
                    setError("Network error sending move to Lichess.");
                    applyGameState(lastReceivedMovesRef.current, initialFenRef.current);
                }
                return false;
            }
        },
        [lichessClient, currentGame, chess, applyGameState]
    );

    sendMoveToLichessRef.current = sendMoveToLichess;

    const onGameFull = useCallback((event: GameFullEvent) => {
        console.info("GameFull event:", event);
        const initFen = event.initialFen || "startpos";
        initialFenRef.current = initFen;

        if (currentGame.color === "white") {
            setPlayer({ username: event.white?.name || me.username });
            setPlayerRating(event.white?.rating ?? null);
            setPlayerTitle(event.white?.title ?? null);

            setOpponent({ username: event.black?.name || currentGame.opponent?.username || "Opponent" });
            setOpponentRating(event.black?.rating ?? currentGame.opponent?.rating ?? null);
            setOpponentTitle(event.black?.title ?? currentGame.opponent?.title ?? null);
        } else {
            setPlayer({ username: event.black?.name || me.username });
            setPlayerRating(event.black?.rating ?? null);
            setPlayerTitle(event.black?.title ?? null);

            setOpponent({ username: event.white?.name || currentGame.opponent?.username || "Opponent" });
            setOpponentRating(event.white?.rating ?? currentGame.opponent?.rating ?? null);
            setOpponentTitle(event.white?.title ?? currentGame.opponent?.title ?? null);
        }

        if (event.state?.wtime !== undefined) {
            setWhiteTimeMs(event.state.wtime);
        } else if (event.clock?.initial !== undefined) {
            setWhiteTimeMs(event.clock.initial);
        }

        if (event.state?.btime !== undefined) {
            setBlackTimeMs(event.state.btime);
        } else if (event.clock?.initial !== undefined) {
            setBlackTimeMs(event.clock.initial);
        }

        applyGameState(event.state?.moves || "", initFen);
    }, [currentGame.color, me.username, currentGame.opponent, applyGameState]);

    const onGameState = useCallback((event: GameStateEvent) => {
        console.info("GameState event:", event);
        if (event.wtime !== undefined) {
            setWhiteTimeMs(event.wtime);
        }
        if (event.btime !== undefined) {
            setBlackTimeMs(event.btime);
        }
        applyGameState(event.moves || "", initialFenRef.current);
    }, [applyGameState]);

    const onGameFullRef = useRef(onGameFull);
    onGameFullRef.current = onGameFull;
    const onGameStateRef = useRef(onGameState);
    onGameStateRef.current = onGameState;

    // Load current game and subscribe to stream
    useEffect(() => {
        setError(null);
        setPlayer({ username: me.username });
        setOpponent({ username: currentGame.opponent?.username || "Opponent" });

        const abortController = new AbortController();

        (async () => {
            try {
                await lichessClient.streamBoardGame(
                    currentGame.gameId,
                    (evt) => onGameFullRef.current(evt),
                    (evt) => onGameStateRef.current(evt),
                    null,
                    null,
                    abortController.signal
                );
            } catch (err: any) {
                if (abortController.signal.aborted) return;
                console.error("Board game stream error:", err);
                setError("Failed to load or stream game data.");
            }
        })();

        return () => {
            abortController.abort();
        };
    }, [currentGame.gameId, lichessClient, me.username, currentGame.opponent]);

    // Update game status on fen change
    useEffect(() => {
        if (!chess) return;
        if (chess.isCheckmate()) setGameStatus("Checkmate! Game over.");
        else if (chess.isDraw()) setGameStatus("Draw!");
        else if (chess.isStalemate()) setGameStatus("Stalemate!");
        else if (chess.isCheck()) setGameStatus(`Check! ${chess.turn() === "w" ? "White" : "Black"} to move.`);
        else setGameStatus(`${chess.turn() === "w" ? "White" : "Black"} to move.`);
    }, [fen, chess]);

    // Handle piece drop (move) on web board
    const onPieceDrop = ({ piece, sourceSquare: from, targetSquare: to }: PieceDropHandlerArgs): boolean => {
        if (!to || from === to) return false;
        if ((piece.pieceType === "wP" && to.slice(1) === "8") ||
            (piece.pieceType === "bP" && to.slice(1) === "1")) {
            setPromotionMove({ from: from as Square, to: to as Square });
            setPromotionVisible(true);
            return false;
        }

        try {
            const move = chess.move({ from, to, promotion: undefined });
            if (!move) {
                return false;
            }
            const newLastMove: LastMove = { from: from as Square, to: to as Square };
            setLastMove(newLastMove);
            setHistory((prev) => [...prev, from + to]);
            const newFen = chess.fen();
            setFen(newFen);
            clearSelection();
            sendBoardSetup(newFen, false, newLastMove);
            sendMoveToLichess(from, to, null);
            return true;
        } catch {
            return false;
        }
    };

    // Handle promotion piece selection
    const onPromotionSelect = (piece: string) => {
        if (!promotionMove) return;
        const { from, to } = promotionMove;

        try {
            const move = chess.move({ from, to, promotion: piece });
            if (move) {
                const newLastMove: LastMove = { from: from as Square, to: to as Square, promotion: piece as any };
                setPromotionVisible(false);
                setPromotionMove(null);
                setLastMove(newLastMove);
                setHistory((prev) => [...prev, `${from}${to}${piece}`]);
                const newFen = chess.fen();
                setFen(newFen);
                clearSelection();
                sendBoardSetup(newFen, false, newLastMove);
                sendMoveToLichess(from, to, piece);
                return true;
            }
        } catch (err) {
            console.error("Promotion move error:", err);
            setPromotionVisible(false);
            setPromotionMove(null);
        }
        return false;
    };

    // Resign the current game
    const onResign = async () => {
        if (!currentGame) return;
        if (!window.confirm("Are you sure you want to resign?")) return;
        try {
            await lichessClient.resignGame(currentGame.gameId);
            setGameStatus("You resigned the game.");
        } catch (err) {
            console.error(err);
            setError("Failed to resign.");
        }
    };

    // Offer a draw (stub)
    const onOfferDraw = () => {
        alert("Draw offer sent (functionality to implement).");
    };

    if (!currentGame) {
        return <Typography>No active game.</Typography>;
    }

    const isWhiteTurn = chess.turn() === "w";
    const isMyTurn = (isWhiteTurn && playerColor === "white") || (!isWhiteTurn && playerColor === "black");
    const isOpponentTurn = !isMyTurn && !chess.isGameOver();

    const playerTimeMs = playerColor === "white" ? whiteTimeMs : blackTimeMs;
    const opponentTimeMs = playerColor === "white" ? blackTimeMs : whiteTimeMs;

    return (
        <Paper
            elevation={3}
            sx={{
                backgroundColor: "#22272e",
                color: "white",
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                maxWidth: 720,
                mx: "auto",
                border: "1px solid rgba(255,255,255,0.1)",
            }}
        >
            {/* Top Navigation Bar with Gallery Back Icon */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
                pb={1.5}
                borderBottom="1px solid rgba(255,255,255,0.1)"
            >
                <Box display="flex" alignItems="center" gap={1.5}>
                    {onBackToGrid && (
                        <Tooltip title="Back to active games grid" arrow>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<GridView />}
                                onClick={onBackToGrid}
                                sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                                All Games
                            </Button>
                        </Tooltip>
                    )}
                    <Typography variant="h6" fontWeight="bold">
                        vs {opponent?.username || "Opponent"}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                        label={isMyTurn ? "Your Turn" : "Opponent's Turn"}
                        color={isMyTurn ? "success" : "default"}
                        size="small"
                        sx={{ fontWeight: 600 }}
                    />
                    <Chip
                        label={`${playerColor === "white" ? "⚪ White" : "⚫ Black"} (${currentGame.speed || "Game"})`}
                        variant="outlined"
                        size="small"
                        sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}
                    />
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Game Status Banner */}
            <Box
                sx={{
                    py: 1,
                    px: 2,
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: isMyTurn ? "rgba(34, 197, 94, 0.15)" : "rgba(255, 255, 255, 0.05)",
                    border: isMyTurn ? "1px solid rgba(34, 197, 94, 0.4)" : "1px solid rgba(255, 255, 255, 0.1)",
                    textAlign: "center",
                }}
            >
                <Typography variant="subtitle1" fontWeight={600} color={isMyTurn ? "success.light" : "text.secondary"}>
                    {gameStatus}
                </Typography>
            </Box>

            {/* Opponent User Header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={1}
                mb={1.5}
                sx={{ maxWidth: 540, mx: "auto" }}
            >
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography component="div" variant="body1" fontWeight="bold" display="flex" alignItems="center" gap={0.5}>
                        <span>{playerColor === "white" ? "⚫ " : "⚪ "}</span>
                        {opponentTitle && (
                            <Chip
                                label={opponentTitle}
                                size="small"
                                color="warning"
                                sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }}
                            />
                        )}
                        <span>{opponent?.username || "Opponent"}</span>
                        {opponentRating !== null && (
                            <Typography component="span" variant="body2" color="text.secondary">
                                ({opponentRating})
                            </Typography>
                        )}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <ChessTimer timeMs={opponentTimeMs} isActiveTurn={isOpponentTurn} />
                </Box>
            </Box>

            {/* Chessboard */}
            <Box sx={{ width: "100%", maxWidth: 540, mx: "auto" }}>
                <Chessboard
                    options={{
                        position: fen,
                        boardOrientation: playerColor,
                        onPieceDrop: onPieceDrop,
                        squareStyles: customSquareStyles,
                        canDragPiece: ({ piece }: PieceHandlerArgs) =>
                            !chess.isGameOver() &&
                            piece.pieceType[0] === playerColor[0] &&
                            chess.turn() === playerColor[0],
                        boardStyle: {
                            borderRadius: "8px",
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.5)",
                        },
                        darkSquareStyle: { backgroundColor: "#4b7399" },
                        lightSquareStyle: { backgroundColor: "#eaedf1" },
                    }}
                />
            </Box>

            {/* Player User Footer */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={1}
                mt={1.5}
                sx={{ maxWidth: 540, mx: "auto" }}
            >
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography component="div" variant="body1" fontWeight="bold" display="flex" alignItems="center" gap={0.5}>
                        <span>{playerColor === "white" ? "⚪ " : "⚫ "}</span>
                        {playerTitle && (
                            <Chip
                                label={playerTitle}
                                size="small"
                                color="primary"
                                sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }}
                            />
                        )}
                        <span>{player?.username || me.username} (You)</span>
                        {playerRating !== null && (
                            <Typography component="span" variant="body2" color="text.secondary">
                                ({playerRating})
                            </Typography>
                        )}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <ChessTimer timeMs={playerTimeMs} isActiveTurn={isMyTurn && !chess.isGameOver()} />
                </Box>
            </Box>

            {/* Game Action Buttons & Board Sync */}
            <Box
                sx={{
                    mt: 2.5,
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                    justifyContent: "center",
                    flexWrap: "wrap",
                }}
            >
                {connected && (
                    <Chip
                        label="Physical Board Synced"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontWeight: 600, py: 1.75, px: 0.5 }}
                    />
                )}
                <Button variant="outlined" color="error" size="small" onClick={onResign}>
                    Resign
                </Button>
                <Button variant="outlined" size="small" sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }} onClick={onOfferDraw}>
                    Offer Draw
                </Button>
            </Box>

            {/* Move History */}
            {history.length > 0 && (
                <Box sx={{ mt: 2.5, maxHeight: 120, overflowY: "auto", color: "#ccc", bgcolor: "rgba(0,0,0,0.2)", p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ mb: 0.5, display: "block", color: "text.secondary", fontWeight: "bold" }}>
                        Move history:
                    </Typography>
                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                        {[...Array(Math.ceil(history.length / 2))].map((_, i) => (
                            <li key={i}>
                                {history[i * 2] + (history.length > i * 2 + 1 ? " - " + history[i * 2 + 1] : "")}
                            </li>
                        ))}
                    </ol>
                </Box>
            )}

            {/* Promotion Dialog */}
            <Dialog open={promotionVisible} onClose={() => setPromotionVisible(false)} maxWidth="xs" fullWidth>
                <DialogContent
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 2,
                        cursor: "pointer",
                        backgroundColor: theme.palette.background.default,
                    }}
                >
                    {[queen, rook, bishop, knight].map((imgSrc, i) => {
                        const pieceMap = ["q", "r", "b", "n"];
                        return (
                            <IconButton
                                key={pieceMap[i]}
                                onClick={() => onPromotionSelect(pieceMap[i])}
                                sx={{ width: 60, height: 60 }}
                                aria-label={`Promote to ${pieceMap[i].toUpperCase()}`}
                            >
                                <img src={imgSrc} alt={pieceMap[i]} style={{ width: 40, height: 40 }} />
                            </IconButton>
                        );
                    })}
                </DialogContent>
            </Dialog>
        </Paper>
    );
};

export default LichessGame;
